'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { List, ChevronDown, ChevronUp } from 'lucide-react'

// ── Types ──────────────────────────────────────────────────────
interface HeadingItem {
  id: string
  text: string
  level: 2 | 3
}

interface TableOfContentsProps {
  /** Raw HTML content string containing h2 and h3 headings */
  htmlContent: string
}

// ── Utility: Slugify ───────────────────────────────────────────
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')   // remove special chars
    .replace(/\s+/g, '-')       // spaces → hyphens
    .replace(/-+/g, '-')        // collapse multiple hyphens
    .replace(/^-|-$/g, '')      // trim leading/trailing hyphens
}

// ── Utility: Strip HTML tags from inner content ───────────────
function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim()
}

// ── Exported Helper: Inject IDs into headings ──────────────────
/**
 * Adds id attributes to h2 and h3 tags in an HTML string.
 * Existing id attributes are preserved; only headings without
 * ids will receive auto-generated slugified ids.
 */
export function injectHeadingIds(html: string): string {
  // 1. Inject IDs into headings
  let result = html.replace(
    /<(h[23])(\s[^>]*)?>([\s\S]*?)<\/\1>/gi,
    (match, tag: string, attrs: string | undefined, inner: string) => {
      const attrStr = attrs || ''
      // If already has an id, return unchanged
      if (/\bid\s*=/i.test(attrStr)) return match

      const text = stripTags(inner)
      const id = slugify(text)
      return `<${tag}${attrStr} id="${id}">${inner}</${tag}>`
    }
  )

  // 2. Wrap <table> elements in scroll wrapper for responsive tables
  result = result.replace(
    /<table([\s\S]*?)<\/table>/gi,
    (match) => `<div class="article-table-wrapper">${match}</div>`
  )

  return result
}

// ── Parse headings from HTML ──────────────────────────────────
function parseHeadings(html: string): HeadingItem[] {
  const headings: HeadingItem[] = []
  const regex = /<(h[23])(?:\s[^>]*)?>([\s\S]*?)<\/\1>/gi
  let match: RegExpExecArray | null

  while ((match = regex.exec(html)) !== null) {
    const tag = match[1].toLowerCase()
    const level = tag === 'h2' ? 2 : 3
    const inner = match[0].replace(/^<h[23][^>]*>/i, '').replace(/<\/h[23]>$/i, '')
    const text = stripTags(inner)
    if (text) {
      headings.push({ id: slugify(text), text, level })
    }
  }

  return headings
}

// ── Component ──────────────────────────────────────────────────
export default function TableOfContents({ htmlContent }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<HeadingItem[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const mobileContentRef = useRef<HTMLDivElement>(null)

  // Parse headings from HTML content
  useEffect(() => {
    setHeadings(parseHeadings(htmlContent))
  }, [htmlContent])

  // Set up IntersectionObserver for active section tracking
  useEffect(() => {
    if (headings.length === 0) return

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const callback: IntersectionObserverCallback = (entries) => {
      // Find the topmost visible heading
      const visibleEntries = entries.filter((e) => e.isIntersecting)
      if (visibleEntries.length > 0) {
        // Use the one closest to the top of the viewport
        const topEntry = visibleEntries.reduce((closest, entry) =>
          entry.boundingClientRect.top < closest.boundingClientRect.top
            ? entry
            : closest
        )
        setActiveId(topEntry.target.id)
      }
    }

    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    // Observe each heading element by id
    headings.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observerRef.current?.observe(el)
    })

    return () => {
      observerRef.current?.disconnect()
    }
  }, [headings])

  // Smooth scroll to heading
  const scrollToHeading = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) {
      const yOffset = -100 // Account for fixed header
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
      setActiveId(id)
      setIsOpen(false) // Close mobile menu after click
    }
  }, [])

  if (headings.length === 0) return null

  const navContent = (
    <nav aria-label="Table of contents">
      <ul className="space-y-1">
        {headings.map((heading) => {
          const isActive = activeId === heading.id
          return (
            <li key={heading.id} className="relative">
              {/* Active indicator bar */}
              <div
                className="absolute left-0 top-0 bottom-0 w-[2px] rounded-full"
                style={{
                  background: '#EF4444',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                  transition: 'opacity 0.2s ease, transform 0.2s ease',
                }}
              />

              <button
                onClick={() => scrollToHeading(heading.id)}
                className="w-full text-left cursor-pointer transition-colors duration-200"
                style={{
                  paddingLeft: heading.level === 3 ? '1.5rem' : '0.75rem',
                  paddingRight: '0.5rem',
                  paddingTop: '0.375rem',
                  paddingBottom: '0.375rem',
                  fontSize: heading.level === 3 ? '0.8125rem' : '0.875rem',
                  fontWeight: heading.level === 2 ? 500 : 400,
                  color: isActive ? '#EF4444' : '#A1A1AA',
                  background: 'transparent',
                  border: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = '#D4D4D8'
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = '#A1A1AA'
                }}
              >
                <span className="line-clamp-2">{heading.text}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )

  return (
    <>
      {/* Desktop: Sticky sidebar */}
      <div
        className="hidden lg:block"
        style={{ maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}
      >
        <div className="glass-card p-5">
          <h4
            className="flex items-center gap-2 text-sm font-semibold text-white mb-4 pb-3"
            style={{ borderBottom: '1px solid var(--border-default)' }}
          >
            <List size={16} className="text-red-500" />
            Table of Contents
          </h4>
          {navContent}
        </div>
      </div>

      {/* Mobile: Collapsible toggle */}
      <div className="lg:hidden mb-6">
        <div className="glass-card overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between p-4 cursor-pointer"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            <span className="flex items-center gap-2">
              <List size={16} className="text-red-500" />
              Table of Contents
            </span>
            {isOpen ? (
              <ChevronUp size={16} className="text-zinc-400" />
            ) : (
              <ChevronDown size={16} className="text-zinc-400" />
            )}
          </button>

          <div
            ref={mobileContentRef}
            style={{
              overflow: 'hidden',
              maxHeight: isOpen ? '80vh' : '0',
              opacity: isOpen ? 1 : 0,
              transition: 'max-height 0.3s ease-in-out, opacity 0.25s ease-in-out',
            }}
          >
            <div
              className="px-4 pb-4"
              style={{ borderTop: '1px solid var(--border-default)' }}
            >
              <div className="pt-3">
                {navContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
